import { QueryClient } from '@tanstack/react-query';

import { queries } from '../../../common/api/queryKeys/queries';
import { AblyService } from '../../../common/services/AblyService';
import { OfferModel } from '../../../offer/model/OfferModel';
import { OfferState } from '../../../offer/model/OfferState';

interface WaitForDirectOrderOfferRequest {
    queryClient: QueryClient;
    orderId: string;
    customerId: string;
    supplierId: string;
    signal: AbortSignal;
}

export const waitForDirectOrderOffer = async ({
    queryClient,
    orderId,
    customerId,
    supplierId,
    signal,
}: WaitForDirectOrderOfferRequest): Promise<OfferModel> => {
    signal.throwIfAborted();
    const channel = AblyService.getClient()?.channels.get(`customers:${customerId}:orders:${orderId}:offers`);
    let cleanup = () => {};

    return new Promise<OfferModel>((resolve, reject) => {
        let active = true;
        const checkOffers = () => {
            if (!active || signal.aborted) {
                return;
            }
            queryClient
                .fetchQuery({ ...queries.offers.list({ orderIds: [orderId] }), staleTime: 0, retry: false })
                .then((offers) => {
                    const matchingOffers = offers.filter(
                        (offer) =>
                            offer.orderId === orderId &&
                            offer.supplierId === supplierId &&
                            [OfferState.ENQUIRY, OfferState.QUOTED].includes(offer.state)
                    );
                    if (matchingOffers.length > 1) {
                        reject(new Error('Multiple supplier offers found'));
                    } else if (matchingOffers.length === 1) {
                        resolve(matchingOffers[0]);
                    }
                }, reject);
        };
        const cancel = () => reject(signal.reason);
        const timeout = setTimeout(() => reject(new Error('Supplier offer creation timed out')), 30_000);
        // Keep a fallback for missed events and disconnected realtime clients.
        const fallback = setInterval(checkOffers, 5000);
        signal.addEventListener('abort', cancel, { once: true });
        cleanup = () => {
            active = false;
            clearTimeout(timeout);
            clearInterval(fallback);
            signal.removeEventListener('abort', cancel);
            channel?.unsubscribe(checkOffers);
        };

        void channel?.subscribe(checkOffers).then(checkOffers, checkOffers);
        checkOffers();
    }).finally(() => cleanup());
};
